// ===============================================================
// ProBank 15V — Install App Prompt (PWA)
//
// Shows the install banner + floating pill on the landing page.
// Hides itself when:
//   - the site is opened inside the ProBank 15V Android app
//     (the app marks itself in the user agent), or
//   - the app / PWA is already installed on this device.
//
// The service worker is registered in index.js — not here.
// ===============================================================

import React, { useEffect, useState } from "react";

let deferredInstallPrompt = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event;
    emit();
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    emit();
  });
}

// True when the site is opened inside the ProBank 15V Android app.
function insideNativeApp() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /ProBankApp/i.test(ua);
}

function appIsInstalled() {
  if (typeof window === "undefined") return true;
  if (insideNativeApp()) return true;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true; // iOS standalone
  return false;
}

function isIOS() {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isAndroid() {
  if (typeof window === "undefined") return false;
  return /android/i.test(window.navigator.userAgent || "");
}

const STEPS_IOS = [
  'Tap the Share button at the bottom of Safari. It looks like a square with an arrow pointing up.',
  'Scroll down the share sheet and tap "Add to Home Screen".',
  'Tap "Add" at the top right. ProBank 15V will appear on your home screen like any other app.',
];

const STEPS_ANDROID = [
  "Tap the three-dot menu at the top right of Chrome.",
  'Tap "Install app" or "Add to Home screen".',
  'Tap "Install" to confirm. ProBank 15V will appear on your home screen.',
];

const STEPS_GENERIC = [
  "Open your browser menu.",
  'Look for "Install app" or "Add to Home screen".',
  "Confirm, and ProBank 15V will be added to your device like any other app.",
];

export default function InstallAppPrompt() {
  // NOTE: appIsInstalled() returns true when running inside the
  // ProBank 15V APK, so `installed` starts as true in the app and
  // nothing is ever rendered there — exactly like an installed PWA.
  const [installed, setInstalled] = useState(appIsInstalled());
  const [canPrompt, setCanPrompt] = useState(!!deferredInstallPrompt);
  const [showHow, setShowHow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const sync = () => {
      setInstalled(appIsInstalled());
      setCanPrompt(!!deferredInstallPrompt);
    };

    listeners.add(sync);

    const mq = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
    if (mq && mq.addEventListener) mq.addEventListener("change", sync);

    // If the native Android app is installed, Chrome can tell us —
    // then we hide the prompt even in the browser.
    if (navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps()
        .then((apps) => {
          if (apps && apps.some((a) => a && a.id === "com.probank15v.app")) {
            setInstalled(true);
          }
        })
        .catch(() => {});
    }

    const onScroll = () => setScrolled(window.scrollY > 420);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      listeners.delete(sync);
      if (mq && mq.removeEventListener) mq.removeEventListener("change", sync);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Already installed, or running inside the ProBank 15V app →
  // never show anything again.
  if (installed) return null;

  const handleInstall = async () => {
    if (deferredInstallPrompt) {
      setBusy(true);
      try {
        deferredInstallPrompt.prompt();
        const choice = await deferredInstallPrompt.userChoice;
        if (choice.outcome === "accepted") {
          deferredInstallPrompt = null;
          setInstalled(true);
          setCanPrompt(false);
        } else {
          setShowHow(true);
        }
      } catch {
        setShowHow(true);
      } finally {
        setBusy(false);
      }
      return;
    }
    setShowHow(true);
  };

  const steps = isIOS() ? STEPS_IOS : isAndroid() ? STEPS_ANDROID : STEPS_GENERIC;

  return (
    <>
      {/* ============ Inline banner (sits in the hero) ============ */}
      <div className="mt-8 rounded-lg border border-[#B8935F]/40 bg-[#14171C]/90 backdrop-blur-sm p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-md bg-[#B8935F]/[0.15] border border-[#B8935F]/30 flex items-center justify-center shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C7A472" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" />
              <path d="M12 18h.01" />
              <path d="M12 8v5" />
              <path d="m9.5 10.5 2.5 2.5 2.5-2.5" />
            </svg>
          </div>
          <div className="flex-1" style={{ minWidth: 190 }}>
            <div className="font-semibold text-[15px] text-[#EDEAE3]">
              Install the ProBank 15V app
            </div>
            <div className="text-[12.5px] text-[#9B9DA3] mt-1 leading-relaxed">
              Faster access and instant alerts, straight from your home screen.
              Free, and no app store needed.
            </div>
          </div>
          <button
            onClick={handleInstall}
            disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#B8935F] text-[#0B0D10] font-semibold rounded hover:bg-[#C7A472] transition text-sm shrink-0 disabled:opacity-70"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {busy ? "Opening..." : "Install App"}
          </button>
        </div>
      </div>

      {/* ============ Floating pill (appears after scrolling) ============ */}
      {scrolled && !showHow && (
        <button
          onClick={handleInstall}
          disabled={busy}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-[#B8935F] text-[#0B0D10] font-semibold text-sm shadow-lg shadow-black/40 hover:bg-[#C7A472] transition disabled:opacity-70"
          style={{ border: "1px solid rgba(255,255,255,0.15)" }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Install App
        </button>
      )}

      {/* ============ Guided steps modal ============ */}
      {showHow && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-5"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setShowHow(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-[#B8935F]/30 bg-[#14171C] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-11 h-11 rounded-md bg-[#B8935F]/[0.15] border border-[#B8935F]/30 flex items-center justify-center mb-4">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C7A472" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <path d="M12 18h.01" />
              </svg>
            </div>
            <div className="font-semibold text-[16px] text-[#EDEAE3]">
              Install ProBank 15V on {isIOS() ? "your iPhone" : "your phone"}
            </div>
            <div className="text-[12.5px] text-[#9B9DA3] mt-1 mb-5 leading-relaxed">
              It takes a few seconds and works like any other app.
            </div>
            <ol className="flex flex-col gap-3 mb-6">
              {steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#B8935F]/[0.15] border border-[#B8935F]/30 flex items-center justify-center text-[#C7A472] font-bold text-[11px] shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[13px] text-[#D5D2C9] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
            <button
              onClick={() => setShowHow(false)}
              className="w-full py-3 rounded bg-[#B8935F] text-[#0B0D10] font-semibold text-sm hover:bg-[#C7A472] transition"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
